import { Formik, FormikProps } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Box,
  Button,
  MIN_WIDTH,
  Modal,
  VStack,
  useMedia,
} from '@vivaahealth/design-system';

import * as api from '../../../api';

import { EditGuarantorSchema, EditGuarantorValues } from './schema';
import { EditGuarantorFormFields } from './EditGuarantorFormFields';

interface Props {
  guarantor: api.Guarantor.PrevisitGuarantor;
  onClose: () => void;
  onSubmit: (editGuarantorValues: EditGuarantorValues) => void;
}

export const EditGuarantorModal = ({ guarantor, onClose, onSubmit }: Props) => {
  const { t } = useTranslation(['guarantorVerification', 'common']);
  const isDesktop = useMedia(MIN_WIDTH.MD);

  const initialValues: EditGuarantorValues = {
    firstName: guarantor.firstName,
    lastName: guarantor.lastName,
    homePhone: guarantor.phoneNumberHome,
    mobilePhone: guarantor.phoneNumberMobile,
    address: guarantor.address,
    relationshipToPatient: guarantor.relationshipToPatient,
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = (values: EditGuarantorValues) => {
    onSubmit(values);
  };

  const generateErrorMessage = useCallback(
    (errorTypeIntlKey: string, fieldNameIntlKey: string) => {
      return t(errorTypeIntlKey, {
        fieldName: t(fieldNameIntlKey, { ns: 'common' }),
      });
    },
    [t],
  );

  return (
    <Modal
      open={true}
      onClose={handleClose}
      title={t('editGuarantorModalTitle')}
      size="large"
    >
      <VStack gap={24} paddingBottom={isDesktop ? 0 : 48}>
        <Alert variant="info">{t('editGuarantorModalAlert')}</Alert>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={EditGuarantorSchema(generateErrorMessage)}
        >
          {(formikProps: FormikProps<EditGuarantorValues>) => {
            return (
              <VStack gap={isDesktop ? 24 : 32}>
                <EditGuarantorFormFields formikProps={formikProps} />
                <Box
                  flexDirection={isDesktop ? 'row-reverse' : 'column'}
                  gap={isDesktop ? 12 : 16}
                >
                  <Button
                    disabled={
                      !formikProps.isValid ||
                      !formikProps.dirty ||
                      formikProps.isSubmitting
                    }
                    fullWidth={!isDesktop}
                    size={isDesktop ? 'small' : 'medium'}
                    onClick={formikProps.submitForm}
                    variant="primary"
                  >
                    {t('saveUpdates', { ns: 'common' })}
                  </Button>
                  <Button
                    fullWidth={!isDesktop}
                    size={isDesktop ? 'small' : 'medium'}
                    onClick={handleClose}
                    variant="outlined"
                  >
                    {t('cancel', { ns: 'common' })}
                  </Button>
                </Box>
              </VStack>
            );
          }}
        </Formik>
      </VStack>
    </Modal>
  );
};
